<script>
  import { onMount } from "svelte";
  import tooltip from "../js/tooltip";
  import Icon from "./Icons.svelte";
  import ShareButton from './ShareButton.svelte';

  export let dataset;
  export let filteredData;
  export let row;

  // console.log(filteredData);
  /* ------------------------------------------------------ */
  /*                       Pagination                       */
  /* ------------------------------------------------------ */

  // Pagination state
  export let currentPage = 1;
  export let itemsPerPage = 25;

  // Compute the paginated data
  $: paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  /* ------------------------------------------------------ */
  /*                         Sorting                        */
  /* ------------------------------------------------------ */
  $: sortClass = "inactive";

  const sortByColumns = [
    "headline",
    "sector",
    "state",
    "date posted",
  ];

  $: sortBy = { col: "headline", ascending: true };

  $: sort = (e, column) => {
    column = column.toLowerCase().replace(/\s/g, "_"); // replace spaces using regex with underscore
    const iconsActive = document.querySelectorAll(".sort-icon--active");
    iconsActive.forEach((icon) => {
      icon.classList.remove("sort-icon--active");
    });

    if (sortBy.col == column) {
      sortBy.ascending = !sortBy.ascending;
      sortClass = sortBy.ascending ? "active" : "inactive";
    } else {
      sortClass = "inactive";
      sortBy.col = column;
      sortBy.ascending = true;
    }

    // Modifier to sorting function for ascending or descending
    let sortModifier = sortBy.ascending ? 1 : -1;

    // Define sorting logic for each column
    let sortFunction;

    if (column === "headline") {
      sortFunction = (a, b) =>
        a.timelineEvent.headline < b.timelineEvent.headline
          ? -1 * sortModifier
          : a.timelineEvent.headline > b.timelineEvent.headline
            ? 1 * sortModifier
            : 0;
    } else if (column === "sector") {
      sortFunction = (a, b) => {
        let sectorA = a.sectors[0] || ""; // Fallback to empty string if undefined
        let sectorB = b.sectors[0] || "";
        return sectorA.localeCompare(sectorB) * sortModifier;
      };
    } else if (column === "state") {
      sortFunction = (a, b) => {
        let stateA = a.states[0] || ""; // Fallback to empty string if undefined
        let stateB = b.states[0] || "";
        return stateA.localeCompare(stateB) * sortModifier;
      };
    } else if (column === "resource_type") {
      sortFunction = (a, b) => a.type.localeCompare(b.type) * sortModifier;
    } else if (column === "date_posted") {
      sortFunction = (a, b) =>
        a.date < b.date
          ? -1 * sortModifier
          : a.date > b.date
            ? 1 * sortModifier
            : 0;
    }

    // Apply the sorting function
    if (sortFunction) {
      // Use a temporary variable for sorted data
      const sortedData = [...filteredData].sort(sortFunction);
      filteredData = sortedData;
    }
  };

  /* ------------------------------------------------------ */
  /*              Dynamic headers and row data              */
  /* ------------------------------------------------------ */

  let filterCategory = dataset.filterCategory;
  let filterValue = dataset.filterValue;

  // default header names
  let headerNames = [
    "Headline",
    "Sector",
    "State",
    "Date Posted",
  ];

  // Reactive statement to update headerNames based on filterCategory and filterValue
  $: {
    if (filterCategory === "states") {
      headerNames = ["Headline", "Sector", "Date Posted"];
    } else if (filterCategory === "sectors" && !filterValue) {
      headerNames = [
        "Headline",
        "Sector",
        "State",
        "Date Posted",
      ];
    } else if (filterCategory === "sectors" && filterValue) {
      headerNames = ["Headline", "State", "Date Posted"];
    } else if (filterCategory === "newsletter") {
      headerNames = ["Headline", "Sector", "State", "Date Posted"];
    }
  }

  // Mapping of headerNames to data keys
  const headerToDataKey = {
    Headline: "timelineEvent.headline",
    Sector: "sectors",
    State: "states",
    "Resource Type": "type",
    "Date Posted": "date",
  };

  // Helper function to get data for a given header
  function getDataForHeader(header, rowData) {
    const key = headerToDataKey[header];
    const keys = key.split(".");
    let data = rowData;

    keys.forEach((k) => {
      data = data && data[k] !== undefined ? data[k] : "";
    });

    // Special handling for "Sector" header to include subsectors
    if (header === "Sector" && Array.isArray(data)) {
      const sectors = data.join(", "); // Convert sectors array to a string
      const subsectors = rowData.subsectors
        ? rowData.subsectors.join(", ")
        : "";
      // Create a string with sectors in bold and subsectors normally
      return `<strong>${sectors}</strong>${
        subsectors ? ", " + subsectors : ""
      }`;
    }

    // Handle other headers normally
    // Special handling for arrays (like states)
    return Array.isArray(data) ? data.join(", ") : data;
  }

  function isFeatured(rowData) {
    if (rowData.is_featured === "") {
      return false;
    } else {
      return rowData.is_featured;
    }
  }

  function WeeklyOrQuarterly(rowData) {
    if (rowData.weeklyOrQuarterly === "") {
      return false;
    } else {
      return rowData.weeklyOrQuarterly;
    }
  }
  /* ------------------------------------------------------ */
  /*                  handleClick, onMount                  */
  /* ------------------------------------------------------ */
  function handleClick(e) {
    let title = undefined;
    let currentRow = undefined;
    let extraContent = undefined;

    if (e.target.parentNode.classList.contains("title")) {
      title = e.target.parentNode;
      currentRow = title.nextElementSibling;
      extraContent = e.target.parentNode.nextElementSibling;
    } else {
      title = e.target.parentNode.parentNode;
      currentRow = title.nextElementSibling;
      extraContent = e.target.parentNode.parentNode.nextElementSibling;
    }

    title.classList.toggle("title--active");
    title.classList.toggle("table__body__cell--border");
    currentRow.classList.toggle("table__body__cell--border");
    // Show/Hide extraContent
    extraContent.classList.toggle("active");
    extraContent.classList.toggle("hide");
    row.isOpen ? (row.isOpen = true) : (row.isOpen = !row.isOpen);
  }

  onMount(() => {
    const iconsActive = document.querySelectorAll(".sort-icon--active");
    iconsActive.forEach((icon) => {
      icon.classList.remove("sort-icon--active");
    });

    // Sync horizontal scroll of table header and table body
    // Inspired by https://codepen.io/Goweb/pen/rgrjWx
    const scrollSync = () => {
      const tableHeader = document.querySelector("#table-header");
      const tableBody = document.querySelector("#table-body");

      const bindSyncScrolling = (one, two) => {
        let echo = false;
        const sync = (elOne, elTwo) => () =>
          (echo = !echo) &&
          ((elOne.scrollTop = elTwo.scrollTop),
          (elOne.scrollLeft = elTwo.scrollLeft));
        two.onscroll = sync(one, two);
        one.onscroll = sync(two, one);
      };
      bindSyncScrolling(tableHeader, tableBody);
    };
    scrollSync();
  });

  // quick fix to use only in dev. we need to removed it when merge into production
  // function fixURLforDevEnv(URL) {
  //   const newBaseUrl = 'https://dev--indian-states-v2.netlify.app'
  //   return URL.replace('https://indianstates.csis.org', newBaseUrl)
  // }
</script>

<div class="table__wrapper">
  <div class="table__container table__container--sticky" id="table-header">
    <table class="table">
      <thead>
        <tr class="table__header-row">
          {#each headerNames as name}
            <th class="table__cell--header" scope="col">
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div
                class="table__cell--header__container table__cell--header__container__{name
                  .toLowerCase()
                  .split(' ')
                  .join('-')}"
                on:click={sortByColumns.includes(name.toLowerCase())
                  ? (e) => sort(e, name)
                  : ""}
              >
                <span>{name}</span>
                {#if sortByColumns.includes(name.toLowerCase())}
                  <div
                    class="sort-icons-container"
                    on:click={(e) => sort(e, name)}
                  >
                    <button
                      class="sort-icon sort-icon--{sortBy.col ==
                        name.toLowerCase().split(' ').join('_') &&
                      sortBy.ascending
                        ? 'inactive'
                        : 'active'}">▲</button
                    >
                    <button
                      class="sort-icon sort-icon--{sortBy.col ==
                        name.toLowerCase().split(' ').join('_') &&
                      sortBy.ascending
                        ? 'active'
                        : 'inactive'}">▼</button
                    >
                  </div>
                {/if}
              </div>
            </th>
          {/each}
        </tr>
      </thead>
    </table>
  </div>
  <div class="table__container" id="table-body">
    <table class="table table__body">
      <tbody>
        {#each paginatedData as rows}
          <tr
            on:click={(e) => handleClick(e)}
            class="title table__body__cell--border"
          >
            {#each headerNames as header}
              {#if header === "Headline"}
                <td class="table__body__cell table__body__cell--data">
                  <div class="table__body__cell__title-container">
                    <span class="icon-container" />
                    {getDataForHeader(header, rows)}
                  </div>
                  {#if rows.type == "Article"}
                    <div class="link" id="in-detail">
                      <span id="detail-text">IN DETAIL:</span><a
                        href={rows.timelineEvent.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        >Read More<span class="icon-container"
                          ><Icon name="Icon-open-blank" class="icon" /></span
                        ></a
                      >
                    </div>
                  {/if}
                  <div class="table__body__cell__tag-container">
                    {#if isFeatured(rows.timelineEvent)}
                      <div>
                        <span class="table__body__cell__is-featured"
                          >{isFeatured(rows.timelineEvent)}</span
                        >
                      </div>
                    {/if}
                    {#if WeeklyOrQuarterly(rows.timelineEvent)}
                      <div>
                        <span class="table__body__cell__weekly-or-quarterly"
                          >{WeeklyOrQuarterly(rows.timelineEvent)}</span
                        >
                      </div>
                    {/if}
                  </div>
                </td>
              {:else if header === "Date Posted"}
                <td class="table__body__cell table__body__cell--data">
                  {rows.date_string}
                </td>
              {:else}
                <td class="table__body__cell table__body__cell--data">
                  {@html getDataForHeader(header, rows)}
                </td>
              {/if}
            {/each}
          </tr>
          <!--this tr is the stuff under the dropdown -->
          <tr class="extra-content hide">
            <td class="table__body__cell" colspan="6">
              <div class="extra-content__container">
                <div class="description">
                  {#if filterCategory == "newsletter" && rows.type == "Newsletter"}{#if rows.timelineEvent.authors && rows.timelineEvent.authors.length > 0}
                      <p class="authorLine">
                        By
                        {#each rows.timelineEvent.authors as author, index (author)}
                          <span class="authorName"
                            >{author}{index <
                            rows.timelineEvent.authors.length - 1
                              ? ", "
                              : ""}</span
                          >
                        {/each}
                      </p>
                    {/if}{/if}
                  {#if rows.timelineEvent.content}
                    {#if rows.type != "Newsletter"}<br />{/if}
                    <div class="contentText">{rows.timelineEvent.content}</div>
                    {#if rows.type != "Newsletter"}<br />{/if}
                  {/if}
                  {#if rows.type == "Article"}
                    <hr class="postArticleSourceBreak" />
                  {/if}
                  {#if rows.timelineEvent.sources && rows.timelineEvent.sources.some((source) => source[1] && source[1].trim() !== "")}
                    <div class="link">
                      SOURCE(S):
                      {#each rows.timelineEvent.sources as source, index (source)}
                        {#if source[1]}
                          <!-- Check if source name is present -->
                          {#if source[0]}
                            <!-- Check if source URL is present -->
                            <a
                              href={source[0]}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {source[1]}{index <
                              rows.timelineEvent.sources.length - 1
                                ? ","
                                : ""}
                            </a>
                          {:else}
                            {source[1]}{index <
                            rows.timelineEvent.sources.length - 1
                              ? ", "
                              : ""}
                          {/if}
                        {/if}
                      {/each}
                    </div>
                    {#if rows.type == "Article"}
                      <ul id="share-row">
                        <li class="share-row__item">
                          <ShareButton
                            url={rows.timelineEvent.url}
                            title={rows.timelineEvent.headline}
                            text={rows.timelineEvent.headline}
                          />
                        </li>
                        <li class="share-row__item">
                          <a
                            href="mailto:?subject={rows.timelineEvent
                              .headline}: {rows.timelineEvent
                              .headline}&amp;body={rows.timelineEvent.url}"
                            target="_blank"
                            rel="noopener noreferrer"
                            ><span class="icon-container"
                              ><Icon
                                name="Icon-email"
                                class="icon__article-icon"
                              /></span
                            ></a
                          >
                        </li>
                        <li class="share-row__item">
                          <a
                            href={`${rows.timelineEvent.url}#print`}
                            target="_blank"
                            rel="noopener noreferrer"
                            ><span class="icon-container"
                              ><Icon
                                name="Icon-printer"
                                class="icon__article-icon"
                              /></span
                            ></a
                          >
                        </li>
                      </ul>
                    {/if}
                  {/if}
                  {#if rows.type == "Newsletter"}
                    <div class="link newsletter">
                      <a
                        href={rows.timelineEvent.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        >Read More<span class="icon-container"
                          ><Icon name="Icon-open-blank" class="icon" /></span
                        ></a
                      >
                    </div>
                  {/if}
                </div>
              </div>
            </td>
          </tr>
        {:else}
          <!-- if filters return an empty table -->
          <tr>
            <td class="table__body__cell table__body__cell--no-data" colspan="6"
              ><p class="table__body__cell__no-data__title">0 entries found.</p>
              <p class="table__body__cell__no-data__desc">
                Try changing or removing filters to adjust the results.
              </p>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
</div>

<style lang="scss">
  @use "../scss/abstracts/" as *;
  @use "../scss/components/table";
  :global(.tippy-box[data-theme~="poni"]) {
    @extend %text-style-ui-4;
    color: $color-text-gray-500;
    background-color: $color-background-white;
    padding: rem(6) rem(6) rem(8) rem(6);
    filter: drop-shadow(0px 1px 9px rgba(0, 0, 0, 0.06))
      drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.1));
  }

  :global(
      .tippy-box[data-theme~="poni"][data-placement^="top"]
        > .tippy-arrow::before,
      .tippy-box[data-theme~="poni"][data-placement^="bottom"]
        > .tippy-arrow::before,
      .tippy-box[data-theme~="poni"][data-placement^="left"]
        > .tippy-arrow::before,
      .tippy-box[data-theme~="poni"][data-placement^="right"]
        > .tippy-arrow::before
    ) {
    border-top-color: $color-background-white;
  }
</style>
